import { Hit, HitProps } from '../Hit';
import cn from 'classnames';
import HitV2 from '_components/Products/Hit';
import ProductTakeoverTile from '_components/Products/ProductTakeoverTile';
import {
  FEATURE_FLAG_HIDE_LEGACY_PTT,
  FEATURE_FLAG_HIT_V2_ENABLED,
  FEATURE_FLAG_PTT_V2,
} from '@config/feature-flag';

import styles from './Hits.module.scss';
import {
  ProductTileTakeover,
  TakeoverTilePTT,
  TakeoverTileSize,
  Treatment,
} from '@adapters/prismic/types/plp';
import React, { useMemo, useRef } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { PTT } from '@components/algolia/PTT';
import { useAlgoliaHits } from '@hooks/useAlgoliaHits';
import { useUI } from '@context/GlobalUIContext';
import PlpCrossLinking from '_components/PlpCrossLinking';
import type { PlpCrossLinkingData } from '_components/PlpCrossLinking/types';
import { useGridColumns } from './useGridColumns';

/**
 * How many grid cells precede the cross-linking band. Desktop renders 4 columns
 * and mobile 2, so 8 cells starts desktop row 3 and mobile row 5 off a single
 * number (ED-211). Cells, not products: a PTT occupies cells too, and
 * counting only products pushed the band off the row boundary.
 */
const CROSS_LINKING_AFTER_CELLS = 8;

/** A cross-linking band as it sits in the composed grid array. */
type CrossLinkingEntry = { cross_linking: PlpCrossLinkingData };

/** Every entry the composed grid can hold, before the band is inserted. */
type GridEntry = HitProps | ProductTileTakeover;

/**
 * Columns an entry occupies, mirroring `.searchHitsItemWide` in
 * `Hits.module.scss`: size-two takeover tiles are double-width at every
 * multi-column breakpoint, everything else is a single cell.
 */
const getColumnSpan = (item: GridEntry, columns: number) => {
  const wide = Math.min(2, columns);

  if (item?.hasOwnProperty('product_tile')) {
    const takeover = item as ProductTileTakeover;
    return takeover.treatment === Treatment.takeoverTile &&
      takeover.product_tile.size === TakeoverTileSize.two
      ? wide
      : 1;
  }

  return 1;
};

/**
 * Index at which the band can be spliced in so it opens a row, or -1 when the
 * grid has no such boundary left. Walks the composed entries tracking the cells
 * consumed so far, including the cells a wrapping double-width entry leaves
 * empty at the end of its row.
 */
const findCrossLinkingIndex = (items: GridEntry[], columns: number) => {
  let cellsUsed = 0;

  return items.findIndex((item) => {
    const span = getColumnSpan(item, columns);
    const cellsLeftInRow = columns - (cellsUsed % columns);

    // A double-width entry that no longer fits its row wraps to the next one,
    // so the cells it skips still count towards the row boundary.
    if (span > cellsLeftInRow) {
      cellsUsed += cellsLeftInRow;
    }

    if (cellsUsed >= CROSS_LINKING_AFTER_CELLS && cellsUsed % columns === 0) {
      return true;
    }

    cellsUsed += span;
    return false;
  });
};

/**
 * Renders a list of search hits along with optional product tile takeovers.
 *
 * @param locale - The locale is passed through and used for displaying pricing.
 * @param pttTiles - An array of Product Tile Takeovers.
 * @param crossLinking - Merch-curated cross-linking band, or null when the PLP has none.
 * @returns The rendered search hits component.
 */

export const Hits = ({
  locale,
  pttTiles,
  crossLinking = null,
}: {
  locale?: string;
  pttTiles: ProductTileTakeover[];
  crossLinking?: PlpCrossLinkingData | null;
}) => {
  const { items, sendEvent } = useAlgoliaHits();
  // Merch-curated entries — PTTs and the cross-linking band — are authored
  // against the first page's grid, so they show on page 1 only rather than
  // repeating down every paginated page (ED-209). Derived from the live page
  // rather than latched, so paging back to the first page brings them back.
  // Read from `results` rather than the `usePagination` connector: this is a
  // read, and a second pagination widget in the tree would register itself
  // with the index.
  const { results } = useInstantSearch();
  const isFirstPage = (results?.page ?? 0) === 0;
  // The band opens a row at the current column count, so its position has to be
  // recomputed when the grid reflows.
  const columns = useGridColumns();
  const { openVideoModal } = useUI();
  const pttVideoRef = useRef<HTMLLIElement>(null);

  const HitVersion = FEATURE_FLAG_HIT_V2_ENABLED ? HitV2 : Hit;

  // Takeover and legacy tiles are gated independently (QA round 2: Jason
  // wanted the ability to hide legacy PTTs without that being tied to
  // FEATURE_FLAG_PTT_V2) rather than one flag switching between them, so all
  // four combinations — neither, either, or both — are possible. The
  // position-based splice and removeBacktoBackPTTs below key off "is this a
  // PTT-shaped item" generically, not off which treatment it is, so mixing
  // both treatments in the same grid doesn't break either of those.
  const activePttTiles = useMemo(
    () =>
      isFirstPage
        ? pttTiles.filter((ptt) =>
            ptt.treatment === Treatment.takeoverTile
              ? FEATURE_FLAG_PTT_V2
              : !FEATURE_FLAG_HIDE_LEGACY_PTT
          )
        : [],
    [pttTiles, isFirstPage]
  );

  const allItems = useMemo(() => {
    let hitsWithPTTs: Array<
      HitProps | ProductTileTakeover | CrossLinkingEntry
    > = [...items];

    activePttTiles
      .filter((ptt) => ptt.position !== null)
      .sort((a, b) => parseInt(a.position) - parseInt(b.position))
      .forEach((ptt) => {
        hitsWithPTTs.splice(parseInt(ptt.position) - 1, 0, ptt);
      });

    const removeBacktoBackPTTs = (
      xs: Array<HitProps | ProductTileTakeover | CrossLinkingEntry>
    ) =>
      xs.filter((item, index, arr) => {
        const isPTT = !!item?.hasOwnProperty('product_tile');
        const previousItemIsPTT =
          !!arr[index - 1]?.hasOwnProperty('product_tile');
        return !isPTT || !previousItemIsPTT;
      });

    // Filtering items can cause PTTs to be back-to-back, so we remove the proceeding ones
    hitsWithPTTs = removeBacktoBackPTTs(hitsWithPTTs);

    // Web merch will set the PTT position to '99' if they want it to always appear last
    const pinnedLastPTT = activePttTiles.find((ptt) => ptt.position === '99');

    if (pinnedLastPTT && !hitsWithPTTs.includes(pinnedLastPTT)) {
      // We don't want to have side by side PTTs, UNLESS it's the pinned one and the first one
      const secondLastItem = hitsWithPTTs[hitsWithPTTs.length - 1];
      if (
        secondLastItem?.hasOwnProperty('product_tile') &&
        'position' in secondLastItem &&
        secondLastItem.position !== activePttTiles[0]?.position
      ) {
        hitsWithPTTs.pop();
      }
      hitsWithPTTs.push(pinnedLastPTT);
    }

    // Inserted last, on the first row boundary at or after CROSS_LINKING_AFTER_CELLS
    // cells. Because the band claims a whole row, inserting it mid-row would wrap
    // it and leave the rest of that row empty. When no boundary is left the band
    // is dropped rather than becoming a de-facto footer below the last row.
    if (crossLinking && isFirstPage) {
      const insertAt = findCrossLinkingIndex(
        hitsWithPTTs as GridEntry[],
        columns
      );

      if (insertAt !== -1) {
        hitsWithPTTs.splice(insertAt, 0, {
          cross_linking: crossLinking,
        });
      }
    }

    return hitsWithPTTs;
  }, [items, activePttTiles, crossLinking, isFirstPage, columns]);

  const shouldDisplay = (index: number) => index < 8;

  return (
    <div className={styles.searchHitsRoot}>
      <ol className={styles.searchHitsList}>
        {allItems.map((hitOrPTT, i) => {
          if (hitOrPTT.hasOwnProperty('product_tile')) {
            const takeover = hitOrPTT as ProductTileTakeover;

            if (takeover.treatment === Treatment.takeoverTile) {
              const tile = takeover as TakeoverTilePTT;
              return (
                <li
                  key={`${tile.product_tile.id}-${tile.position}`}
                  className={cn(styles.searchHitsItem, {
                    [styles.newHit]: FEATURE_FLAG_HIT_V2_ENABLED,
                    [styles.searchHitsItemWide]:
                      tile.product_tile.size === TakeoverTileSize.two,
                  })}
                >
                  <ProductTakeoverTile
                    ptt={tile}
                    isPriority={shouldDisplay(i)}
                  />
                </li>
              );
            }

            const ptt = takeover as Exclude<
              ProductTileTakeover,
              TakeoverTilePTT
            >;
            return (
              <li
                key={`${ptt.product_tile.id}-${ptt.position}`}
                className={cn(styles.searchHitsItem, {
                  [styles.newHit]: FEATURE_FLAG_HIT_V2_ENABLED,
                })}
                ref={ptt.product_tile.videoId ? pttVideoRef : null}
                data-video-id={ptt.product_tile.videoId}
                data-video-title={ptt.product_tile.videoTitle}
              >
                {ptt.product_tile.videoId ? (
                  <button
                    className={styles.buttonWrapper}
                    onClick={() =>
                      ptt.product_tile.videoId &&
                      openVideoModal({
                        videoId: ptt.product_tile.videoId,
                        videoHeight: ptt.product_tile.videoHeight,
                        videoWidth: ptt.product_tile.videoWidth,
                      })
                    }
                  >
                    <PTT ptt={ptt} isPriority={shouldDisplay(i)} />
                  </button>
                ) : (
                  <PTT ptt={ptt} isPriority={shouldDisplay(i)} />
                )}
              </li>
            );
          }
          if (hitOrPTT.hasOwnProperty('cross_linking')) {
            const { cross_linking: band } = hitOrPTT as CrossLinkingEntry;
            return (
              <li
                className={styles.searchHitsCrossLinking}
                key={`cross-linking-${band.id}`}
              >
                <PlpCrossLinking data={band} locale={locale} />
              </li>
            );
          }
          const hit = hitOrPTT as HitProps;
          return (
            <li
              key={hit.objectID}
              className={cn(styles.searchHitsItem, {
                [styles.newHit]: FEATURE_FLAG_HIT_V2_ENABLED,
              })}
            >
              <HitVersion
                hit={hit}
                locale={locale}
                isPriority={shouldDisplay(i)}
                sendEvent={sendEvent}
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
};
